import { memo, useState, useRef, useEffect } from 'react'
import type { TimelineItem } from '@/types/timeline'
import type { FamilyMember } from '@/types/family'
import type { Task, TaskContext } from '@/types/task'
import { formatTimeLong, formatTimeRangeLong, inferMealTime } from '@/lib/timeUtils'
import { isSameDay } from '@/lib/dateUtils'
import { SchedulePopover, type ScheduleContextItem } from '@/components/triage'
import { useScheduleActionsContext } from '@/contexts/ScheduleActionsContext'
import { AssigneeDropdown, MultiAssigneeDropdown } from '@/components/family'
import { Video, Check, Pencil, Hourglass, ListChecks, ChevronUp, ChevronDown, ChevronRight, MessageCircle, AlertCircle, Mail, Car, Target } from 'lucide-react'
import { requestDiscussionOpen } from '@/lib/discussions/openIntent'
import { ScheduleItemItems } from './ScheduleItemItems'
import { RowActionRail } from './RowActionRail'
import { RoutineMoveButton } from './RoutineMoveButton'
import { ROW_SHELL, ROW_GRID, LANE, MARK, UNDER_TITLE } from './todayRowGrid'
import { useMobile } from '@/hooks/useMobile'
import { TaskCheckbox } from './TaskCheckbox'
import { ExpandingPanel } from './ExpandingPanel'
import { DOMAIN_COLORS } from '@/lib/domainColors'
import { rowSubtitle } from '@/lib/rowSubtitle'
import { TimelineSpine } from './TimelineSpine'
import { locationLink } from '@/lib/locationLink'
import { useTravelTime } from '@/hooks/useTravelTime'


interface ScheduleItemProps {
  item: TimelineItem
  selected?: boolean
  /**
   * @deprecated Today, trimmed (2026-10-14): the hover bulk-select checkbox is
   * gone — several things at once is something you say ("Plan my day"). These
   * four are accepted and ignored until TodaySectionList stops passing them.
   */
  bulkSelectable?: boolean
  /** @deprecated ignored — see `bulkSelectable`. */
  bulkSelected?: boolean
  /** @deprecated ignored — see `bulkSelectable`. */
  showBulkAffordance?: boolean
  /** @deprecated ignored — see `bulkSelectable`. */
  onToggleBulkSelect?: () => void
  onSelect: () => void
  onToggleComplete: () => void
  /** Completes ONE per-person item under the block. Subtasks are tasks, so
   *  callers pass the same task-completion handler the parent row uses. */
  onToggleSubtask?: (subtaskId: string) => void
  onToggleWaiting?: () => void
  onPush?: (target: Date | 'week' | 'month' | 'quarter') => void
  onSchedule?: (date: Date, isAllDay: boolean) => void
  onSkip?: () => void
  contactName?: string
  /**
   * Projects are hidden from the product (2026-09-02, see Sidebar.tsx) so the
   * row no longer draws either of these. They stay on the props because the
   * data and every caller still carry them — hide the noun, keep the model.
   */
  projectName?: string
  projectId?: string
  parentTaskName?: string
  parentTaskId?: string
  onOpenParentTask?: (taskId: string) => void
  // Family member assignment
  familyMembers?: FamilyMember[]
  assignedTo?: string | null
  onAssign?: (memberId: string | null) => void
  // Multi-member assignment (for events)
  assignedToAll?: string[]
  onAssignAll?: (memberIds: string[]) => void
  // Context assignment (work/family/personal)
  onContextChange?: (context: TaskContext | undefined) => void
  /** @deprecated ignored — it fed the row ⋯ menu's "Flag for discussion…",
   *  which now lives in the item panel as "Bring up" (Today, trimmed,
   *  2026-10-14). */
  onUpdateDiscussion?: (next: { needsDiscussion: boolean; discussionNote?: string }) => void
  // Overdue styling
  isOverdue?: boolean
  overdueLabel?: string
  // Schedule context for the schedule popover
  getScheduleItemsForDate?: (date: Date) => ScheduleContextItem[]
  // Panel state (for smart close behavior)
  panelOpen?: boolean
  onClosePanel?: () => void
  // Coaching indicator
  hasCoaching?: boolean
  // Visual weight variant
  variant?: 'full' | 'minimal'
  // Hide time label (for same-time grouping) — preserves column space
  hideTime?: boolean
  /** Draw the timeline spine reaching UP from this row's marker to the row above. */
  spineAbove?: boolean
  /** Draw the timeline spine reaching DOWN from this row's marker to the row below. */
  spineBelow?: boolean
  /**
   * @deprecated ignored. It carried the hover when-chip (TaskTimingMenu) on
   * the title line; a task's day and time now change in the item panel, which
   * draws the same control (Today, trimmed, 2026-10-14).
   */
  belowTitleAccessory?: React.ReactNode
  /** What this task serves — "↳ Step toward “goal”" or "↳ From “line”" —
   *  drawn at rest under the title, on phones too. When given it replaces the
   *  bare goal-name line (it says the same thing, and also reaches a line the
   *  task was copied down from). Today supplies it; other hosts don't. */
  servesLine?: React.ReactNode
  /** The signed-in member. Initials are household INFORMATION only when they
   *  say something the reader doesn't know — someone else's work. Work that
   *  is yours carries none (Today, 2026-09-21). */
  currentMemberId?: string | null
}

// Warm muted color tokens for overdue styling
const overdueColors = {
  warning50: 'hsl(38 50% 96%)',
  warning500: 'hsl(35 45% 55%)',
  warning600: 'hsl(32 40% 52%)',
}

// Domain context colors - shared utility (kept for future use)
const _contextColors: Record<string, { dot: string; bg: string }> = DOMAIN_COLORS

export const ScheduleItem = memo(function ScheduleItem({
  item,
  selected,
  onSelect,
  onToggleComplete,
  onToggleSubtask,
  onToggleWaiting,
  onPush,
  onSchedule,
  contactName,
  parentTaskName,
  parentTaskId,
  onOpenParentTask,
  familyMembers = [],
  assignedTo,
  onAssign,
  assignedToAll = [],
  onAssignAll,
  onContextChange,
  isOverdue,
  overdueLabel,
  getScheduleItemsForDate,
  panelOpen,
  onClosePanel,
  hasCoaching,
  variant = 'full',
  hideTime,
  spineAbove,
  spineBelow,
  servesLine,
}: ScheduleItemProps) {
  const isMobile = useMobile()
  // Needed-today mark: STATE, so it lives with the title chips. The item
  // panel sets it ("Need today"); the chip here only clears it. A mark
  // expires by ceasing to match the VIEWED day (src/lib/today/neededToday.ts),
  // not the real calendar day — nothing ever clears `neededOn` — so compare
  // against ctx.viewedDate. Fall back to "now" only when no provider supplies
  // it (e.g. a test rendering ScheduleItem in isolation).
  const { onSetNeededToday, viewedDate } = useScheduleActionsContext()
  const isNeededToday = !!item.neededOn && isSameDay(item.neededOn, viewedDate ?? new Date())
  // Hover state powers the smooth expanding banner (location-only metadata row).
  // On mobile we never expand — preserves the pre-existing behavior where
  // these were never visible without hover.
  const [isHovered, setIsHovered] = useState(false)
  // "18 min drive" for a row with a real address — null for a video meeting,
  // an unset home address, or anything already done. Cached per address, so a
  // recurring destination is looked up once, not once per render.
  const travelLabel = useTravelTime(item, { enabled: !item.completed && !item.skipped })
  const isTask = item.type === 'task'
  const isRoutine = item.type === 'routine'
  const isEvent = item.type === 'event'
  /** Informational-only: no prep/handoff expected, so it's never actionable
   *  and never gets a check circle/checkbox. See eventFree.ts. */
  const isFree = !!item.isFree
  const isActionable = (isTask || isRoutine || isEvent) && !isFree // Events are now checkable
  /** This row came out of a capture (a forwarded school email). */
  const fromEmail = !!item.captureId
  /**
   * Per-person items: subtasks that name a person, plus every open subtask of a
   * row extracted from an email. Unlike plain steps these are the CONTENT of
   * the block — "Picture Day" means nothing without "Liam: collared shirt" —
   * so they render inline, always, at every width. Completed ones drop out:
   * what is left is what still has to happen.
   */
  // A step given to the person who already carries the row names no one new:
  // it is an ordinary step and folds behind the disclosure (Scott, 2026-10-06
  // — five "SK" steps under Scott's own task held the page open).
  const rowPeople = new Set([assignedTo, ...assignedToAll].filter(Boolean))
  const isPerPerson = (s: Task) => fromEmail || (!!s.assignedTo && !rowPeople.has(s.assignedTo))
  const perPersonItems = (item.originalTask?.subtasks ?? []).filter(
    (s) => !s.completed && isPerPerson(s),
  )
  const hasPerPersonItems = perPersonItems.length > 0
  /**
   * Everything the per-person list does NOT cover — ordinary steps, which stay
   * behind the disclosure. Split on the per-person PREDICATE, not on the
   * rendered list: a per-person item that has been completed drops out of the
   * inline list, and it must not reappear here as if it were a plain step.
   */
  const plainSubtasks = (item.originalTask?.subtasks ?? []).filter((s) => !isPerPerson(s))
  /** Anything rendering beneath the title makes the title column taller — the
   *  leading columns then need pinning to the title's first line. */
  const hasBelowTitleContent = !!servesLine
    || !!(item.goalLabel && !item.completed)
    || !!(item.isWaiting && item.waitingFor && !item.completed)
    || hasPerPersonItems
    || fromEmail
  /** Anyone assigned, you included: their avatars show at rest (Scott,
   *  2026-10-04). An unassigned row is everyone's and no one's. */
  // Only someone the household knows: an id with no member behind it drew a
  // blank grey people icon at rest (Boxing, Marta — 2026-10-06).
  const assigned = [assignedTo, ...assignedToAll].some((id) => !!id && (familyMembers.length === 0 || familyMembers.some((m) => m.id === id)))

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    // Always close panel if open
    if (panelOpen && onClosePanel) {
      onClosePanel()
    }

    // Then perform the complete action
    onToggleComplete()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't hijack Enter/Space when focus is inside a text-entry element
    // (e.g., a popover textarea like DiscussionPicker's). Without this guard,
    // typing a space in any text field nested under the row would select the
    // row instead of inserting the space.
    const target = e.target as HTMLElement
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect()
    }
  }

  // Parse time display
  const getTimeDisplay = () => {
    // All-day items - check for meal keyword inference first
    if (item.allDay) {
      const inferred = inferMealTime(item.title)
      if (inferred && item.startTime) {
        // Show inferred time for meal events (e.g., "6:30 PM" for dinner)
        const inferredDate = new Date(item.startTime)
        inferredDate.setHours(inferred.hour, inferred.minute, 0, 0)
        return { type: 'single' as const, time: formatTimeLong(inferredDate) }
      }
      return { type: 'allday' as const }
    }

    if (!item.startTime) return null

    if (item.endTime) {
      const rangeStr = formatTimeRangeLong(item.startTime, item.endTime, item.allDay)
      if (rangeStr === 'All day') {
        return { type: 'allday' as const }
      }
      const [start, end] = rangeStr.split('|')
      return { type: 'range' as const, start, end }
    }

    return { type: 'single' as const, time: formatTimeLong(item.startTime) }
  }

  const timeDisplay = getTimeDisplay()

  const hasContactChip = !!contactName
  // The steps disclosure is for plain subtasks only — the per-person items are
  // already on screen, always open, so a chip revealing the same names again is
  // a second, contradicting affordance. It used to be `!hasPerPersonItems &&
  // …`, which let ONE assigned subtask hide every plain step beside it. The two
  // populations coexist: the chip now counts what the inline list does not
  // cover, and reads its counts from the split rather than from the row's
  // whole-subtask totals.
  const stepsTotal = hasPerPersonItems ? plainSubtasks.length : (item.subtaskCount ?? 0)
  const stepsDone = hasPerPersonItems
    ? plainSubtasks.filter((s) => s.completed).length
    : (item.subtaskCompletedCount ?? 0)
  const hasSubtasks = stepsTotal > 0
  /** Provenance, rendered in the subtitle slot at EVERY width. */
  const emailBadge = (
    <span className="inline-flex items-center gap-1 align-middle">
      <Mail className="w-3 h-3 shrink-0" aria-hidden />
      From an email
    </span>
  )
  // Steps disclosure — collapsed by default. Local because it is pure view
  // state and each row opens independently.
  const [stepsOpen, setStepsOpen] = useState(false)


  // ── Mobile card render ────────────────────────────────────────────────────
  // Matches the mockup: each row is a discrete card with a left time column,
  // a tinted icon block, a title + small context line, and a right cluster
  // (assignee, more). Desktop render falls through below.
  if (isMobile) {
    // Time displayed stacked: "1:00 / PM" for single times, with a hyphen row
    // for ranges ("6:30 / – / 7:15 / PM").
    const splitTime = (s: string) => {
      const m = s.match(/^(.+?)\s?(AM|PM)$/i)
      return m ? [m[1], m[2].toUpperCase()] : [s, '']
    }
    const renderStackedTime = () => {
      if (!timeDisplay) return null
      if (timeDisplay.type === 'allday') {
        // An ordinary untimed task has no time to show; the column is simply
        // empty. Only an all-day EVENT says so.
        return isTask ? null : <span className="text-[11px] font-medium text-neutral-400">All day</span>
      }
      if (timeDisplay.type === 'range') {
        const [s1, p1] = splitTime(timeDisplay.start)
        const [s2] = splitTime(timeDisplay.end)
        return (
          <>
            <div>{s1}</div>
            <div>–</div>
            <div>{s2}</div>
            <div>{p1}</div>
          </>
        )
      }
      const timeStr = timeDisplay.time ?? ''
      const [hm, ap] = splitTime(timeStr)
      return (
        <>
          <div>{hm}</div>
          {ap && <div>{ap}</div>}
        </>
      )
    }

    // Small context line: category, then location. (Project was the first
    // preference until Projects was hidden — 2026-09-02, see Sidebar.tsx.)
    const contextLabel = (item.category && item.category !== 'task' ? item.category : null) || item.location || null

    // Swipe gestures: right → complete, left → edit (open detail panel).
    // The card translates with the finger; coloured action panels behind it
    // reveal under the card. Past the commit threshold, releasing fires the
    // action; otherwise the card snaps back.
    const SWIPE_COMMIT_PX = 80
    const SWIPE_MAX_PX = 140
    return (
      <ScheduleItemMobileCard
        swipeMaxPx={SWIPE_MAX_PX}
        swipeCommitPx={SWIPE_COMMIT_PX}
        onCompleteSwipe={() => { if (!isFree) handleCheckboxClick({ stopPropagation: () => {} } as React.MouseEvent) }}
        onEditSwipe={onSelect}
        onTap={onSelect}
        // Centred is right for a one-line row and wrong the moment the inline
        // per-person items (or the "From an email" line) make the title column
        // tall — the time and the type tile drift to the vertical middle of the
        // card, away from the title they label. Same rule the desktop branch
        // applies to its leading column via hasBelowTitleContent/self-start.
        cardClassName={`
          schedule-mobile-row relative flex ${hasPerPersonItems || fromEmail || stepsOpen ? 'items-start' : 'items-center'} gap-3 bg-bg-elevated rounded-2xl border border-neutral-200/70
          px-3 py-3 shadow-card
          ${selected ? 'ring-2 ring-primary-300 shadow-md' : ''}
          ${item.completed || item.skipped || isFree ? 'opacity-60' : ''}
        `}
      >
        {/* Left time column — stacked. An untimed task leaves it empty rather
            than reserving a column of "All day" down the page. */}
        {(timeDisplay && !(timeDisplay.type === 'allday' && isTask)) && (
          <div className="w-10 shrink-0 text-[11px] font-medium text-neutral-500 leading-tight tabular-nums text-left">
            {renderStackedTime()}
          </div>
        )}

        {/* The one completion control every row shares — a circle you can tap,
            the same on a phone as on the desktop. It replaced the tinted type
            tile (Today, 2026-09-21): the tile drew a different glyph per kind
            of thing, and none of them did anything. A chosen row carries a
            small dot beside it. */}
        {/* The cell is the touch target's full 48px both ways. A phone-wide
            rule makes every block hide its x-overflow, which turns a smaller
            cell into a scroll container: a 20px-wide one showed half a
            circle, a 31px-tall one grew a scrollbar beside it. The negative
            margin keeps the circle itself where the time column's text
            starts. */}
        {/* Completing is not opening: the checkbox's click stays out of the card's tap. */}
        <div className="w-12 h-12 -ml-3 shrink-0 flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
          {item.focused && (
            <span aria-hidden="true" title="Chosen for today" className="row-focus-dot absolute left-1 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-sage-500" />
          )}
          {isActionable ? (
            <TaskCheckbox
              completed={item.completed}
              isWaiting={isTask ? item.isWaiting : undefined}
              onToggleComplete={() => handleCheckboxClick({ stopPropagation: () => {} } as React.MouseEvent)}
              onToggleWaiting={() => onToggleWaiting?.()}
              shape="circle"
            />
          ) : null}
        </div>

        {/* Title + context line. The title is the row's keyboard/screen-reader
            way in (VoiceOver takes over swipes); its click bubbles to the
            card's tap handler, which opens the detail. */}
        <div className="flex-1 min-w-0">
          <button
            type="button"
            // Whole titles on the phone, as in the native app — a clipped
            // title hides what the task actually is.
            className={`block w-full text-left text-[16px] font-medium leading-snug break-words ${item.completed || item.skipped ? 'line-through text-neutral-400' : 'text-neutral-800'}`}
          >
            {item.title}
            {item.focused && <span className="sr-only"> · Chosen for today</span>}
          </button>
          {(contextLabel || isFree) && (
            <div className="flex items-center gap-1.5 text-[12px] text-neutral-500 mt-0.5 truncate">
              {/* Free chip — a dimmed mobile row needs its own explanation;
                  there's no hover state on a phone to reveal one. */}
              {isFree && (
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 bg-neutral-100 rounded px-1.5 py-0.5">
                  Free
                </span>
              )}
              {contextLabel && <span className="truncate">{contextLabel}</span>}
              {travelLabel && (
                <span className="shrink-0 inline-flex items-center gap-1 text-neutral-500">
                  <Car className="w-3 h-3 shrink-0" aria-hidden />
                  {travelLabel.replace(/ drive$/, '')}
                </span>
              )}
            </div>
          )}
          {fromEmail && (
            <div className="text-[12px] text-neutral-500 mt-0.5">{emailBadge}</div>
          )}
          {/* What this step is for, on the phone too — a phone has no hover to
              reveal it, so the commitment has to be on the row itself. */}
          {servesLine ? <div className="mt-0.5 min-w-0">{servesLine}</div> : item.goalLabel && !item.completed && (
            <div className="flex items-center gap-1.5 text-[12px] text-neutral-500 mt-0.5 min-w-0">
              <Target className="w-3 h-3 shrink-0" aria-hidden />
              <span className="truncate">{item.goalLabel}</span>
            </div>
          )}
          {/* Per-person items — inline on the phone too. This is where a
              parent actually reads "who needs what tomorrow". */}
          <ScheduleItemItems
            items={perPersonItems}
            members={familyMembers}
            onToggle={onToggleSubtask}
            viewedDate={viewedDate}
          />
          {/* Steps inside the card, as the native app shows them: a quiet
              count that opens the list, each step ticked in place. */}
          {hasSubtasks && (
            <div onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                aria-expanded={stepsOpen}
                onClick={() => setStepsOpen((v) => !v)}
                className="phone-steps-toggle"
              >
                <ListChecks className="w-3.5 h-3.5" aria-hidden="true" />
                {stepsDone} of {stepsTotal} steps
                {stepsOpen ? <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" /> : <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />}
              </button>
              {stepsOpen && plainSubtasks.length > 0 && (
                <ul className="phone-steps">
                  {plainSubtasks.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        aria-pressed={!!s.completed}
                        aria-label={`${s.completed ? 'Reopen' : 'Complete'} ${s.title}`}
                        disabled={!onToggleSubtask}
                        onClick={() => onToggleSubtask?.(s.id)}
                        className={`phone-step-check${s.completed ? ' is-done' : ''}`}
                      >
                        {s.completed && <Check className="w-3 h-3" strokeWidth={3} aria-hidden="true" />}
                      </button>
                      <span className={s.completed ? 'line-through text-neutral-400' : 'text-neutral-700'}>{s.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Right cluster — assignee. Three-dot removed; swipe
            now exposes the same actions (right→complete, left→edit). Needed
            today is a TAP control here, not a third swipe — swipe already
            means complete (right) / edit (left), and a third gesture would be
            undiscoverable. Uses the same isSameDay-against-viewedDate
            definition of "marked" as the desktop chip/menu — bare truthiness
            of item.neededOn was a bug already fixed once there. */}
        <div className="flex items-center gap-1 shrink-0">
          {/* The needed-today mark is STATE: it shows when set (and clears on
              tap), never as a grey control on every row. Setting it lives in
              the row's detail sheet. `!item.completed` matches the desktop
              chip: a done task is not still "needed today". */}
          {isTask && onSetNeededToday && item.originalTask && !item.completed && isNeededToday && (
            <button
              type="button"
              aria-label="Not needed today"
              onClick={(e) => {
                e.stopPropagation()
                onSetNeededToday(item.originalTask!.id, null)
              }}
              className="p-1.5 rounded-lg"
            >
              <AlertCircle className="w-4 h-4 text-amber-500" />
            </button>
          )}
          {/* A routine's one triage verb — move this time or skip it. The
              desktop rail carries it; without it here a phone could only
              swipe a routine done or open it (Scott, 2026-10-01). */}
          {item.type === 'routine' && !item.completed && !item.skipped && (
            <RoutineMoveButton item={item} />
          )}
          {/* Initials whenever someone is assigned, you included. The sheet
              still assigns an unassigned row. */}
          {!assigned ? null : familyMembers.length > 0 && onAssignAll ? (
            <div onClick={(e) => e.stopPropagation()}>
              <MultiAssigneeDropdown
                members={familyMembers}
                selectedIds={assignedToAll}
                onSelect={onAssignAll}
                size="sm"
                label={item.type === 'event' ? "Who's attending?" : "Who's responsible?"}
              />
            </div>
          ) : familyMembers.length > 0 && onAssign ? (
            <div onClick={(e) => e.stopPropagation()}>
              <AssigneeDropdown
                members={familyMembers}
                selectedId={assignedTo}
                onSelect={onAssign}
                size="sm"
              />
            </div>
          ) : null}
        </div>
      </ScheduleItemMobileCard>
    )
  }

  return (
    <div
      data-selectable
      data-row-kind={item.type}
      onClick={() => {
        // Always select this item (switches panel to show this item's details)
        onSelect()
      }}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="button"
      aria-pressed={selected}
      className={`
        group relative cursor-pointer transition-all duration-200 rounded-xl border
        ${variant === 'minimal'
          ? `px-3 py-1 md:py-0.5 md:-mx-[13px] border-transparent hover:bg-neutral-50/60 ${selected ? 'bg-neutral-50 ring-1 ring-neutral-200' : ''}`
          : `${ROW_SHELL} ${selected
              ? 'bg-primary-50 border-primary-200 shadow-md ring-1 ring-primary-200'
              : 'border-transparent hover:bg-primary-50/50 hover:border-primary-100'
            }`
        }
        ${item.completed || item.skipped || isFree ? 'opacity-60' : ''}
      `}
    >
      {/* Main row: time | circle | title | rail — Today's shared grid, so
          these rows line up with RoutineCollectionRow's. (The hover
          bulk-select checkbox that hung in the gutter went with Today,
          trimmed, 2026-10-14: a row is tick, drag and tap.) */}
      <div className={ROW_GRID}>
        <TimelineSpine above={spineAbove} below={spineBelow} />

        {/* Time column - fixed width for alignment */}
        {hideTime ? (
          <div className={`schedule-empty-time ${LANE}`} />
        ) : (isTask && onSchedule) || ((isRoutine || item.type === 'event') && onPush) ? (
          <div
            className={`schedule-time-column ${LANE} relative`}
            onClick={(e) => e.stopPropagation()}
          >
            <SchedulePopover
              value={item.startTime ?? undefined}
              isAllDay={item.allDay}
              onSchedule={(date, isAllDay) => {
                if (onSchedule) {
                  onSchedule(date, isAllDay)
                } else if (onPush) {
                  onPush(date)
                }
              }}
              onClear={onSchedule ? () => {
                onSchedule(undefined as unknown as Date, false)
              } : undefined}
              getItemsForDate={getScheduleItemsForDate}
              skipToTime={true}
              itemTitle={item.title}
              trigger={
                <button
                  className="w-full text-left text-xs font-medium tabular-nums rounded-md px-1 py-0.5 -mx-1 hover:bg-neutral-100 transition-colors cursor-pointer underline decoration-dotted decoration-neutral-300 underline-offset-2 hover:decoration-neutral-500"
                  title="Change time"
                >
                  {isOverdue && overdueLabel ? (
                    <span style={{ color: overdueColors.warning600 }}>
                      {overdueLabel}
                    </span>
                  ) : timeDisplay ? (
                    timeDisplay.type === 'allday' ? (
                      // An untimed task's column is empty on the page (the
                      // label is for a reader who can't see the column);
                      // an all-day event still says so.
                      <span className={isTask ? 'sr-only' : 'text-neutral-400'}>All day</span>
                    ) : timeDisplay.type === 'range' ? (
                      // One time in the lane; "until …" sits under the title.
                      <span className="text-neutral-500">{timeDisplay.start}</span>
                    ) : (
                      <span className="text-neutral-500">{timeDisplay.time}</span>
                    )
                  ) : (
                    <span className="text-neutral-300">—</span>
                  )}
                </button>
              }
            />
          </div>
        ) : (
          <div className={`schedule-time-column ${LANE} text-xs font-medium tabular-nums`}>
            {isOverdue && overdueLabel ? (
              <span style={{ color: overdueColors.warning600 }}>
                {overdueLabel}
              </span>
            ) : timeDisplay ? (
              timeDisplay.type === 'allday' ? (
                <span className={isTask ? 'sr-only' : 'text-neutral-400'}>All day</span>
              ) : timeDisplay.type === 'range' ? (
                <span className="text-neutral-500">{timeDisplay.start}</span>
              ) : (
                <span className="text-neutral-500">{timeDisplay.time}</span>
              )
            ) : (
              <span className="text-neutral-300">—</span>
            )}
          </div>
        )}

        {/* Checkbox/circle/calendar - fixed width for alignment, hidden on mobile when overdue */}
        {/* When something renders beneath the title (suggestion chip, "waiting on"
            line) the title column grows taller, and a centred checkbox drifts
            down, away from the name it belongs to. self-start pins it to the top
            of the row — which IS the title's first line, the title column being
            the tallest child — with a nudge to centre it on that line's box. */}
        {!(isMobile && isOverdue) && (
          <div className={`${MARK} flex items-center justify-center relative z-[1] ${
            hasBelowTitleContent || hasSubtasks ? `self-start ${variant === 'minimal' ? '' : 'mt-0.5'}` : ''
          }`}>
            {/* Chosen for today: a small dot beside the circle, and a label a
                screen reader hears. Nothing louder — the row is already on
                the page because you chose it. */}
            {item.focused && (
              <>
                {/* Named, not decoration (walkthrough 2026-10-02, #25: "an
                    unexplained small green dot"): a tooltip on hover and
                    the same words for a screen reader. */}
                <span role="img" aria-label="Chosen for today" title="Chosen for today" className="today-chosen-dot absolute -left-[15px] top-1/2 -translate-y-1/2 flex h-3 w-3 items-center justify-center">
                  <span className="h-1.5 w-1.5 rounded-full bg-sage-500" />
                </span>
              </>
            )}
            {isEvent && isFree ? (
              // Free events carry no check circle — nothing for a parent to do.
              null
            ) : isActionable ? (
              // One completion control for every kind of row — task, routine
              // or event — a plain circle. The square-for-tasks shape and the
              // domain-tinted ring made the circles the loudest things on the
              // page; the kind of thing a row is belongs to its details.
              <TaskCheckbox
                completed={item.completed}
                isWaiting={isTask ? item.isWaiting : undefined}
                onToggleComplete={() => handleCheckboxClick({ stopPropagation: () => {} } as React.MouseEvent)}
                onToggleWaiting={() => {
                  if (panelOpen && onClosePanel) onClosePanel()
                  onToggleWaiting?.()
                }}
                isRoutine={isRoutine}
                shape="circle"
              />
            ) : null}
          </div>
        )}

        {/* Title */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`
                row-title flex-1 min-w-0
                text-[16px] leading-snug font-medium line-clamp-2 transition-colors
                ${item.completed || item.skipped
                  ? 'line-through text-neutral-400'
                  : item.isWaiting
                    ? 'text-amber-600/70 italic'
                    : variant === 'minimal'
                    ? 'text-neutral-500 group-hover:text-neutral-700'
                    : 'text-neutral-800 group-hover:text-neutral-900'
                }
              `}
            >
              {item.title}
              {/* Bare marker only when there's no sentence — otherwise the
                  "Waiting on …" line below says it better. */}
              {item.isWaiting && !item.waitingFor && !item.completed && (
                <span className="ml-1.5 text-xs text-amber-500 not-italic font-normal">waiting</span>
              )}
            </span>
            {/* Coaching sparkle indicator — desktop only */}
            {hasCoaching && (
              <span className="hidden md:inline shrink-0 text-amber-400 opacity-60" title="Coaching tips available">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                </svg>
              </span>
            )}
            {/* Flagged for discussion — STATE, so it belongs with the title
                chips, not in the action rail. The control that sets it lives in
                the item panel ("Bring up"). The bubble itself is the door to the
                conversation (Scott, 2026-09-22): it opens the item's panel
                with its Discussion already showing. */}
            {item.needsDiscussion && (
              <button
                type="button"
                className="hidden md:inline-flex shrink-0 items-center text-primary-500 hover:text-primary-700"
                aria-label={item.discussionNote ? `Open discussion: ${item.discussionNote}` : 'Open discussion'}
                title={item.discussionNote ? `Discussion: ${item.discussionNote}` : 'Open discussion'}
                onClick={(e) => {
                  e.stopPropagation()
                  requestDiscussionOpen(item.type, item.id.replace(/^(task|routine|event)-/, ''))
                  onSelect()
                }}
              >
                <MessageCircle className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Needed today mark — STATE, so it belongs with the title chips,
                not the action rail. The item panel sets/clears it; clicking the
                chip itself is a shortcut to clear. */}
            {isNeededToday && !item.completed && (
              <button
                type="button"
                title="Needed today — click to clear"
                onClick={(e) => { e.stopPropagation(); onSetNeededToday?.(item.originalTask!.id, null) }}
                className="hidden md:inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-amber-700 bg-amber-100/70"
              >
                <AlertCircle className="w-3 h-3" aria-hidden />
                Today
              </button>
            )}
            {/* The hover when-chip ("Any time") lived here until Today,
                trimmed (2026-10-14): day and time change in the item panel. */}
          </div>
          {/* Steps fold to their next one (2026-10-06): "Next: … · 5 steps".
              A disclosure, not a label — steps don't earn their own Today
              rows, so this is the way to see them without leaving the page. */}
          {hasSubtasks && (() => {
            const next = !stepsOpen ? plainSubtasks.find((st) => !st.completed) : undefined
            return (
              <div className="hidden md:flex items-baseline gap-1.5 min-w-0 text-[12.5px] leading-tight mt-0.5">
                {next && <span className="truncate text-neutral-500" title={next.title}>Next: {next.title}</span>}
                {next && <span aria-hidden className="text-neutral-300">·</span>}
                <button
                  type="button"
                  aria-expanded={stepsOpen}
                  onClick={(e) => { e.stopPropagation(); setStepsOpen((v) => !v) }}
                  className="inline-flex shrink-0 items-center gap-0.5 font-medium text-primary-700 hover:text-primary-800"
                >
                  {stepsTotal} {stepsTotal === 1 ? 'step' : 'steps'}
                  {stepsOpen ? <ChevronUp className="w-3 h-3" aria-hidden /> : <ChevronRight className="w-3 h-3" aria-hidden />}
                </button>
              </div>
            )
          })()}
          {/* What the wait is ON — its own line beneath the title, never a
              replacement for it. Replacing the title would break scanning: two
              months from now "Guy's response about pizza" won't tell you which
              task it belonged to. */}
          {item.isWaiting && item.waitingFor && !item.completed && (
            <div className="flex items-baseline gap-1.5 text-[12px] text-amber-600/90 leading-tight mt-0.5 min-w-0">
              <Hourglass className="w-3 h-3 shrink-0 translate-y-[1px]" aria-hidden />
              <span className="truncate" title={item.waitingFor}>
                Waiting on {item.waitingFor}
              </span>
            </div>
          )}
          {/* What this step is FOR. A commitment made at the month's altitude
              should still be legible on the day you act on it. No count and no
              progress — just the goal's name. */}
          {servesLine ? <div className="mt-0.5 min-w-0">{servesLine}</div> : item.goalLabel && !item.completed && (
            <div className="flex items-baseline gap-1.5 text-[12px] text-neutral-500 leading-tight mt-0.5 min-w-0">
              <Target className="w-3 h-3 shrink-0 translate-y-[1px]" aria-hidden />
              <span className="truncate" title={item.goalLabel}>{item.goalLabel}</span>
            </div>
          )}
          {/* Subtitle: category + duration, plus provenance. Empty for plain
              tasks. The category/duration half stays desktop-only as before;
              "From an email" shows at every width, so a row carrying it is not
              wrapped in `hidden md:block`. */}
          {(() => {
            const until = timeDisplay?.type === 'range' ? timeDisplay.end : undefined
            const subtitle = [rowSubtitle(item, { until }), travelLabel].filter(Boolean).join(' · ')
            if (!subtitle && !fromEmail) return null
            if (!fromEmail) {
              return (
                <div className="hidden md:block text-[12px] text-neutral-500 leading-tight mt-0.5">
                  {subtitle}
                </div>
              )
            }
            return (
              <div className="text-[12px] text-neutral-500 leading-tight mt-0.5">
                {subtitle && <span className="hidden md:inline">{subtitle} · </span>}
                {emailBadge}
              </div>
            )
          })()}
          {/* Per-person items — always open, every width. Left-aligned WITH the
              title, same as the steps list below. */}
          <ScheduleItemItems
            items={perPersonItems}
            members={familyMembers}
            onToggle={onToggleSubtask}
            viewedDate={viewedDate}
          />
          {/* Steps — revealed by the subtask chip above. Left-aligned WITH the
              title (a block sibling of the whole row would sit under the time
              gutter and read as the next task's content). */}
          {stepsOpen && plainSubtasks.length ? (
            <ul className="hidden md:block mt-1 space-y-0.5 border-l-2 border-neutral-200 pl-3">
              {plainSubtasks.map((s) => (
                <li key={s.id} className="text-[13px] text-neutral-600">
                  <span className={s.completed ? 'line-through text-neutral-400' : ''}>
                    {s.title}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {/* Trailing controls — a FIXED four-slot rail, not a run of conditional
            siblings. See RowActionRail for why: the old version rendered six
            controls on a task, five on an event, three on a routine, so nothing
            formed a column down the page. */}
        <RowActionRail
          item={item}
          onContextChange={onContextChange}
          onAssign={onAssign}
          onAssignAll={onAssignAll}
          familyMembers={familyMembers}
          assignedTo={assignedTo}
          assignedToAll={assignedToAll}
          assigned={assigned}
        />
      </div>

      {/* Metadata row — location on hover, contact/parentTask/Free always compact */}
      {(item.location || hasContactChip || parentTaskName || isFree) && (() => {
        const onlyLocation = !hasContactChip && !parentTaskName && !isFree
        const metadataContent = (
          <div className={`flex items-center gap-2 ${UNDER_TITLE} flex-wrap ${onlyLocation ? 'pt-1' : 'mt-1'}`}>
            {/* Free chip — informational-only: no prep/handoff expected. */}
            {isFree && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500 bg-neutral-100 rounded px-1.5 py-0.5">
                Free
              </span>
            )}

            {/* Location chip — hover-only for cleaner default view.
                URL-shaped locations (Zoom/Meet/Teams) open the meeting URL
                directly; physical locations open Google Maps directions. */}
            {item.location && (() => {
              const link = locationLink(item.location, item.locationPlaceId, item.meetingUrl)
              if (link.kind === 'empty') return null
              const isMeeting = link.kind === 'url' || link.kind === 'virtual'
              const chipClass = 'inline-flex items-center gap-1 px-1.5 py-0.5 text-primary-600 hover:text-primary-700 rounded text-[11px] font-medium transition-all opacity-0 group-hover:opacity-100 max-w-[220px]'
              const icon = isMeeting ? (
                <Video className="w-3 h-3 shrink-0" aria-hidden />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              )
              // A virtual meeting with no join URL is a label, never a directions link.
              if (link.kind === 'virtual') {
                return (
                  <span className={chipClass} title="Video meeting">
                    {icon}
                    <span className="truncate">{item.location}</span>
                  </span>
                )
              }
              return (
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={chipClass}
                  title={isMeeting ? 'Open meeting link' : 'Get directions'}
                >
                  {icon}
                  <span className="truncate">{link.kind === 'url' ? 'Join meeting' : item.location}</span>
                </a>
              )
            })()}

            {/* The travel estimate moved up beside "until …" (2026-10-06):
                still visible at rest, now on the line that says when. */}

            {/* Contact chip - desktop only */}
            {hasContactChip && (
              <span className="hidden md:inline-flex items-center gap-1 text-[11px] text-neutral-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span className="truncate max-w-[80px]">{contactName}</span>
              </span>
            )}

            {/* Parent task context */}
            {parentTaskName && parentTaskId && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenParentTask?.(parentTaskId)
                }}
                className="text-[11px] text-neutral-400 hover:text-neutral-600 hover:underline truncate max-w-[180px]"
              >
                {parentTaskName}
              </button>
            )}
          </div>
        )

        // If location is the only metadata, the row collapses until hover.
        // Wrap in ExpandingPanel so layout shifts smoothly instead of jumping.
        return onlyLocation
          ? <ExpandingPanel open={isHovered && !isMobile}>{metadataContent}</ExpandingPanel>
          : metadataContent
      })()}

    </div>
  )
})

// ─── ScheduleItemMobileCard ──────────────────────────────────────────────────
// Wraps the mobile card with swipe gestures:
//   • drag left  past commit threshold → fire onCompleteSwipe (complete)
//   • drag right past commit threshold → fire onEditSwipe (open detail)
// Coloured action panels reveal underneath as the card slides. Touches that
// move primarily vertically don't engage the swipe (so page scroll still
// works). A tap (no swipe) opens the detail through onTap.

interface ScheduleItemMobileCardProps {
  swipeCommitPx: number
  swipeMaxPx: number
  onCompleteSwipe: () => void
  onEditSwipe: () => void
  /** A plain tap (not the end of a swipe) — opens the detail. */
  onTap: () => void
  cardClassName: string
  children: React.ReactNode
}

function ScheduleItemMobileCard({
  swipeCommitPx,
  swipeMaxPx,
  onCompleteSwipe,
  onEditSwipe,
  onTap,
  cardClassName,
  children,
}: ScheduleItemMobileCardProps) {
  // `dragging` is the only React state — it flips on/off between gestures so
  // the CSS transition class can toggle. Per-frame motion is driven via refs
  // below so the card subtree (including dropdowns) never re-renders mid-drag.
  const [dragging, setDragging] = useState(false)

  const cardEl = useRef<HTMLDivElement>(null)
  const completePanelEl = useRef<HTMLDivElement>(null)
  const editPanelEl = useRef<HTMLDivElement>(null)

  const startX = useRef(0)
  const startY = useRef(0)
  const decided = useRef<'horizontal' | 'vertical' | null>(null)
  const dxRef = useRef(0)
  const rafPending = useRef(false)
  const haptic = useRef(false) // fires once per gesture when crossing commit
  // Track mount so a rAF that survives the component (e.g. a fast swipe-to-
  // complete removes the card from the list mid-gesture) doesn't fire a
  // spurious haptic tick on a row that no longer exists.
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  const paint = () => {
    rafPending.current = false
    if (!mounted.current) return
    const dx = dxRef.current
    if (cardEl.current) {
      cardEl.current.style.transform = `translateX(${dx}px)`
    }
    const intensity = Math.min(1, Math.abs(dx) / swipeCommitPx)
    if (completePanelEl.current) {
      completePanelEl.current.style.opacity = dx < 0 ? String(intensity) : '0'
    }
    if (editPanelEl.current) {
      editPanelEl.current.style.opacity = dx > 0 ? String(intensity) : '0'
    }
    // Light haptic tick on commit-threshold crossing (Android only; no-op on iOS).
    if (!haptic.current && Math.abs(dx) >= swipeCommitPx) {
      haptic.current = true
      navigator.vibrate?.(10)
    }
  }

  const requestPaint = () => {
    if (rafPending.current) return
    rafPending.current = true
    requestAnimationFrame(paint)
  }

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    startX.current = t.clientX
    startY.current = t.clientY
    decided.current = null
    dxRef.current = 0
    haptic.current = false
    swiped.current = false
    setDragging(true)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    const t = e.touches[0]
    const ax = t.clientX - startX.current
    const ay = t.clientY - startY.current
    if (decided.current === null) {
      if (Math.abs(ax) < 6 && Math.abs(ay) < 6) return
      decided.current = Math.abs(ax) > Math.abs(ay) ? 'horizontal' : 'vertical'
    }
    if (decided.current === 'vertical') return // let the page scroll
    dxRef.current = Math.max(-swipeMaxPx, Math.min(swipeMaxPx, ax))
    requestPaint()
  }

  // A swipe's touchend can still produce a click; that click is not a tap.
  const swiped = useRef(false)

  const commit = () => {
    const dx = dxRef.current
    if (decided.current === 'horizontal') {
      swiped.current = true
      // Right-to-left (dx < 0) → Complete. Left-to-right (dx > 0) → Edit.
      if (dx <= -swipeCommitPx) {
        onCompleteSwipe()
      } else if (dx >= swipeCommitPx) {
        onEditSwipe()
      }
    }
    // Snap back. Once `dragging` flips to false on the next React frame, the
    // card gains the transform-transition class, so this looks like a spring.
    dxRef.current = 0
    requestPaint()
    decided.current = null
    setDragging(false)
  }

  return (
    <div className="schedule-mobile-swipe relative mb-3 overflow-hidden rounded-2xl">
      {/* Complete action — right side, revealed on right-to-left swipe. */}
      <div
        ref={completePanelEl}
        aria-hidden
        className="absolute inset-y-0 right-0 w-1/2 flex items-center justify-end pr-5 rounded-r-2xl bg-emerald-500"
        style={{ opacity: 0 }}
      >
        <Check className="w-6 h-6 text-white" />
        <span className="ml-2 text-white text-sm font-medium">Complete</span>
      </div>
      {/* Edit action — left side, revealed on left-to-right swipe. */}
      <div
        ref={editPanelEl}
        aria-hidden
        className="absolute inset-y-0 left-0 w-1/2 flex items-center justify-start pl-5 rounded-l-2xl bg-sky-500"
        style={{ opacity: 0 }}
      >
        <Pencil className="w-5 h-5 text-white" />
        <span className="ml-2 text-white text-sm font-medium">Edit</span>
      </div>

      <div
        ref={cardEl}
        data-selectable
        onClick={() => {
          if (swiped.current) { swiped.current = false; return }
          onTap()
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={commit}
        onTouchCancel={commit}
        className={cardClassName}
        // INVARIANT: `transform` MUST stay a static string literal here.
        // React's style diff only writes properties whose virtual-DOM value
        // changed — because `transform` is never a different string between
        // renders, React never overwrites the imperative value paint() wrote
        // via cardEl.current.style.transform. If you ever make `transform`
        // dependent on a render-time value, React will snap the card back to
        // that value on every re-render mid-drag.
        style={{
          transform: 'translateX(0px)',
          transition: dragging ? 'none' : 'transform 200ms ease-out',
          touchAction: 'pan-y',
        }}
      >
        {children}
      </div>
    </div>
  )
}
